import React, { useState, useEffect } from 'react';
import { Button } from './button';
import { Filter, X } from 'lucide-react';
import { buildYearOptions, getCurrentYear } from '../utils/eventStatus';

const EventFilters = ({
  filters = {},
  events = [],
  onFiltersChange,
  onClearFilters,
  onSearch,
  searchPlaceholder = 'Buscar por título...',
  loading = false
}) => {
  // Estado local para los inputs
  const [localTitle, setLocalTitle] = useState(filters.title || '');
  const [localStatus, setLocalStatus] = useState(filters.status || 'all');
  const [localDateRange, setLocalDateRange] = useState(filters.dateRange || 'all');
  const [localYear, setLocalYear] = useState(filters.year || getCurrentYear());

  const yearOptions = buildYearOptions(events);

  // Sincronizar estado local cuando cambian los filtros externos
  useEffect(() => {
    setLocalTitle(filters.title || '');
    setLocalStatus(filters.status || 'all');
    setLocalDateRange(filters.dateRange || 'all');
    setLocalYear(filters.year || getCurrentYear());
  }, [filters.title, filters.status, filters.dateRange, filters.year]);

  const handleClearFilters = () => {
    setLocalTitle('');
    setLocalStatus('all');
    setLocalDateRange('all');
    setLocalYear(getCurrentYear());
    onClearFilters();
  };

  const handleTitleChange = (e) => {
    const value = e.target.value;
    setLocalTitle(value);
  };

  const handleStatusChange = (e) => {
    const value = e.target.value;
    setLocalStatus(value);
  };

  const handleDateRangeChange = (e) => {
    const value = e.target.value;
    setLocalDateRange(value);
  };

  const handleApplyFilters = () => {
    onFiltersChange({
      ...filters,
      title: localTitle,
      status: localStatus,
      dateRange: localDateRange,
      year: localYear,
      applyNow: true
    });
    onSearch();
  };

  const hasActiveFilters =
    localTitle !== '' ||
    localStatus !== 'all' ||
    localDateRange !== 'all' ||
    localYear !== getCurrentYear();

  return (
    <div className="bg-white p-4 rounded-lg border border-gray-200 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Filter className="w-4 h-4 text-gray-500 mr-2" />
          <h3 className="text-sm font-medium text-gray-700">Filtros de Eventos</h3>
        </div>
        {hasActiveFilters && (
          <Button
            onClick={handleClearFilters}
            variant="outline"
            size="sm"
            className="text-xs"
          >
            <X className="w-3 h-3 mr-1" />
            Limpiar
          </Button>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {/* Búsqueda por título */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Título</label>
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={localTitle}
            onChange={handleTitleChange}
            className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={loading}
          />
        </div>

        {/* Filtro por estado */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
          <select
            value={localStatus}
            onChange={handleStatusChange}
            className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={loading}
          >
            <option value="all">Todos los estados</option>
            <option value="upcoming">Próximos</option>
            <option value="completed">Completados</option>
            <option value="cancelled">Cancelados</option>
          </select>
        </div>

        {/* Filtro por año */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Año</label>
          <select
            value={localYear}
            onChange={(e) => setLocalYear(e.target.value)}
            className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={loading}
          >
            <option value="all">Todos los años</option>
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>

        {/* Filtro por rango de fechas */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Rango de Fechas</label>
          <select
            value={localDateRange}
            onChange={handleDateRangeChange}
            className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={loading}
          >
            <option value="all">Todas las fechas</option>
            <option value="today">Hoy</option>
            <option value="week">Esta semana</option>
            <option value="month">Este mes</option>
            <option value="past">Pasados</option>
            <option value="upcoming">Próximos</option>
          </select>
        </div>

        {/* Botón de aplicar filtros */}
        <div className="flex items-end">
          <Button
            onClick={handleApplyFilters}
            className="w-full h-10 bg-blue-600 hover:bg-blue-700"
            disabled={loading}
          >
            Aplicar Filtros
          </Button>
        </div>
      </div>


    </div>
  );
};

export default EventFilters; 